import React, { useState, useEffect, useMemo } from 'react';
import { DashboardHeader } from './DashboardHeader';
import { Sidebar } from './Sidebar';
import { PermissionChangeListener } from './PermissionChangeListener';
import { UserProfileDrawer } from '../ui/UserProfileDrawer';
import { PasswordChangeModal } from '../auth/PasswordChangeModal';
import { useAuthStore } from '../../store/auth.store';

interface Usuario {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
  requiereCambioContrasena?: boolean;
  roles: any[];
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [profileDrawerConfig, setProfileDrawerConfig] = useState<{ initialTab?: 'perfil' | 'adicionales'; initialEditMode?: boolean }>({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { setUser, user: storeUser } = useAuthStore();

  const openProfileDrawer = (config?: { tab?: 'perfil' | 'adicionales'; editMode?: boolean }) => {
    setProfileDrawerConfig({
      initialTab: config?.tab,
      initialEditMode: config?.editMode,
    });
    setIsProfileDrawerOpen(true);
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Inicializar desde localStorage al montar
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData && userData !== 'undefined') {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && typeof parsedUser === 'object') {
          setUsuario(parsedUser);
          setUser(parsedUser);
        } else {
          console.warn('Invalid user data in localStorage:', parsedUser);
          localStorage.removeItem('userData');
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('userData');
      }
    }
  }, [setUser]);

  // Sincronizar estado local cuando el store global cambia (ej: después de loadUser())
  useEffect(() => {
    if (storeUser) {
      setUsuario(storeUser as Usuario);
    }
  }, [storeUser]);

  // Mostrar modal de cambio de contraseña si es requerido
  useEffect(() => {
    if (usuario?.requiereCambioContrasena) {
      setShowPasswordModal(true);
    }
  }, [usuario?.requiereCambioContrasena]);

  // Memoizar el usuario para evitar re-renders innecesarios
  const memoizedUsuario = useMemo(() => usuario, [usuario?.id, usuario?.email]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header fijo: ocupa solo el ancho a la derecha del sidebar */}
      <DashboardHeader
        usuario={memoizedUsuario || undefined}
        onOpenProfile={openProfileDrawer}
        onToggleMobileMenu={toggleMobileMenu}
        isCollapsed={isCollapsed}
      />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar 
          isCollapsed={isCollapsed} 
          setIsCollapsed={setIsCollapsed}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
        />
        
        {/* Main content area (el padding-top compensa la altura del header fijo) */}
        <main className="flex-1 p-3 md:p-4 lg:p-6 pt-16 lg:pt-20 overflow-auto h-screen">
          {children}
        </main>
      </div>

      {/* Listener global para cambios de permisos */}
      <PermissionChangeListener />

      {/* Drawer de perfil de usuario */}
      <UserProfileDrawer 
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
        initialTab={profileDrawerConfig.initialTab}
        initialEditMode={profileDrawerConfig.initialEditMode}
        onSuccess={() => {
          // Opcional: recargar datos del usuario si es necesario
        }}
      />

      {/* Modal de cambio de contraseña obligatorio */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        isForced={usuario?.requiereCambioContrasena}
      />

    </div>
  );
};
